import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Trash2, FileText, Database, ShoppingBag, AlertCircle, CheckCircle2, Loader2, Eye, Info, RefreshCw, Sparkles, Settings } from 'lucide-react';
import { ProductFeedNormalizationModal } from '@/components/data-sources/ProductFeedNormalizationModal';
import { EnrichmentSettingsModal } from '@/components/data-sources/EnrichmentSettingsModal';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL, SOCKET_URL, UPLOADS_URL } from '@/config/api';
import io from 'socket.io-client';

interface DataSource {
  id: string;
  source_type: 'reviews' | 'google_ads' | 'product_feed';
  file_name: string;
  file_size: number;
  upload_date: string;
  uploaded_by: string;
  status: 'processing' | 'completed' | 'failed';
  records_count: number;
  metadata?: any;
}

interface BatchJob {
  id: string;
  data_source_id: string;
  status: string; // preparing, uploading, submitted, validating, in_progress, finalizing, completed, failed, cancelled
  total_records: number;
  processed_records: number;
  failed_records: number;
  cache_hits?: number;
  pre_existing_embeddings?: number;
  progress_percentage?: number; // Added by backend API
  created_at: string;
  submitted_at?: string;
  completed_at?: string;
  error_message?: string;
}

interface BatchGroup {
  group_id: string;
  data_source_id: string;
  total_records: number;
  total_chunks: number;
  completed_chunks: number;
  failed_chunks: number;
  status: string; // processing, completed, failed, partial
  created_at: string;
  updated_at: string;
  completed_at?: string;
  progress_percentage: number;
}

interface EmbeddingStats {
  source_type: string;
  total_entries: number;
  entries_with_embeddings: number;
  entries_without_embeddings: number;
  completion_percentage: number;
  approved: number;
  pending: number;
}

interface FileEmbeddingStats {
  data_source_id: string;
  file_name: string;
  source_type: string;
  records_count: number;
  kb_entries_created: number;
  entries_with_embeddings: number;
  entries_without_embeddings: number;
  embedding_completion_percentage: number;
  batch_job_id: string | null;
  batch_status: string | null;
  uploaded_at: string;
  has_missing_embeddings: boolean;
}

export default function DataSources() {
  const { selectedSite } = useSite();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([]);
  const [batchGroups, setBatchGroups] = useState<Record<string, BatchGroup>>({});
  const [embeddingStats, setEmbeddingStats] = useState<EmbeddingStats[]>([]);
  const [fileEmbeddingStats, setFileEmbeddingStats] = useState<FileEmbeddingStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const [reconciling, setReconciling] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState('google_ads');
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [reviewSubtype, setReviewSubtype] = useState<string>('customer_reviews');
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [normalizationModalOpen, setNormalizationModalOpen] = useState(false);
  const [enrichmentSettingsOpen, setEnrichmentSettingsOpen] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [progressPhase, setProgressPhase] = useState<string>('');
  const [suspiciousProductsCount, setSuspiciousProductsCount] = useState<number>(0);

  useEffect(() => {
    if (selectedSite) {
      loadDataSources();
    }
  }, [selectedSite]);

  // Poll for batch job status updates and embedding stats
  useEffect(() => {
    if (!selectedSite) return;

    // Check if there are any active batch jobs (including queued/rate_limited)
    const hasActiveBatches = batchJobs.some(job =>
      ['preparing', 'uploading', 'queued', 'submitting', 'submitted', 'validating', 'in_progress', 'finalizing', 'rate_limited'].includes(job.status)
    );

    // Check if there are any incomplete embeddings
    const hasIncompleteEmbeddings = embeddingStats.some(stats =>
      stats.completion_percentage > 0 && stats.completion_percentage < 100
    );

    if (!hasActiveBatches && !hasIncompleteEmbeddings) return;

    // Poll every 30 seconds
    const interval = setInterval(() => {
      loadDataSources();
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedSite, batchJobs, embeddingStats]);

  // Socket.IO for real-time progress updates
  useEffect(() => {
    // Connect to backend Socket.IO
    // Start with polling (more reliable across Docker) then upgrade to websocket
    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: true
    });

    // Listen for KB import progress
    socket.on('kb-import-progress', (data: any) => {
      console.log('[DataSources] KB import progress:', data);
      setProgressPhase(data.phase);
      setProgressMessage(data.message);
      setUploadProgress(prev => ({ ...prev, product_feed: data.progress }));

      // Reload data sources when complete
      if (data.phase === 'completed') {
        setTimeout(() => {
          loadDataSources();
          setProgressMessage('');
          setProgressPhase('');
        }, 2000);
      }
    });

    // Listen for batch progress
    socket.on('batch-progress', (data: any) => {
      console.log('[DataSources] Batch progress:', data);
      setProgressPhase(data.phase);
      setProgressMessage(data.message);

      // Update batch jobs when submitted or completed
      if (data.phase === 'submitted' || data.phase === 'completed') {
        setTimeout(() => {
          loadDataSources();
          // Clear progress message after batches are submitted (they'll process in background)
          setProgressMessage('');
          setProgressPhase('');
        }, 3000);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selectedSite]);

  const loadDataSources = async () => {
    if (!selectedSite) return;

    setLoading(true);
    try {
      // Load data sources
      const response = await authFetch(`${API_URL}/data-sources/list/${selectedSite.id || selectedSite._id}`);
      const data = await response.json();

      if (data.success) {
        setDataSources(data.dataSources);
      }

      // Load batch jobs
      const batchResponse = await authFetch(`${API_URL}/data-sources/batch-jobs/${selectedSite.id || selectedSite._id}`);
      const batchData = await batchResponse.json();

      if (batchData.success) {
        setBatchJobs(batchData.batchJobs);
      }

      // Load embedding stats
      const embeddingResponse = await authFetch(`${API_URL}/embeddings/stats/${selectedSite.id || selectedSite._id}`);
      const embeddingData = await embeddingResponse.json();

      if (embeddingData.success) {
        setEmbeddingStats(embeddingData.stats);
      }

      // Load per-file embedding stats
      const fileEmbeddingResponse = await authFetch(`${API_URL}/embeddings/stats-by-file/${selectedSite.id || selectedSite._id}`);
      const fileEmbeddingData = await fileEmbeddingResponse.json();

      if (fileEmbeddingData.success) {
        setFileEmbeddingStats(fileEmbeddingData.files);
      }

      // Load batch groups for product feeds
      const batchGroupsMap: Record<string, BatchGroup> = {};
      for (const ds of data.dataSources) {
        if (ds.source_type === 'product_feed') {
          try {
            const bgResponse = await authFetch(`${API_URL}/data-sources/batch-group/${ds.id}`);
            const bgData = await bgResponse.json();
            if (bgData.success && bgData.hasBatchGroup) {
              batchGroupsMap[ds.id] = bgData.batchGroup;
            }
          } catch (err) {
            console.error(`Failed to load batch group for ${ds.id}:`, err);
          }
        }
      }
      setBatchGroups(batchGroupsMap);

      // Load suspicious products count for enrichment review badge
      try {
        const suspiciousResponse = await authFetch(`${API_URL}/product-enrichment/suspicious?siteId=${selectedSite.id || selectedSite._id}&limit=1`);
        const suspiciousData = await suspiciousResponse.json();
        if (suspiciousData.success && suspiciousData.stats) {
          setSuspiciousProductsCount(suspiciousData.stats.totalSuspicious || 0);
        }
      } catch (err) {
        console.error('Failed to load suspicious products count:', err);
      }
    } catch (error) {
      console.error('Failed to load data sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (sourceType: string, file: File) => {
    console.log('[DataSources] Upload initiated:', {
      sourceType,
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      hasSelectedSite: !!selectedSite,
      siteId: selectedSite?.id || selectedSite?._id,
      reviewSubtype: sourceType === 'reviews' ? reviewSubtype : undefined
    });

    if (!selectedSite || !file) {
      console.error('[DataSources] Missing required data:', { selectedSite, file });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('siteId', selectedSite.id || selectedSite._id);

    // Add review subtype for review uploads
    if (sourceType === 'reviews') {
      formData.append('reviewSubtype', reviewSubtype);
    }

    // Log FormData contents
    console.log('[DataSources] FormData contents:');
    for (const [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
    }

    setUploading(prev => ({ ...prev, [sourceType]: true }));
    setUploadProgress(prev => ({ ...prev, [sourceType]: 0 }));

    try {
      // Determine endpoint based on source type
      let endpoint = '';
      if (sourceType === 'reviews') {
        endpoint = '/data-sources/upload/reviews';
      } else if (sourceType === 'google_ads') {
        endpoint = '/data-sources/upload/google-ads';
      } else if (sourceType === 'product_feed') {
        endpoint = '/data-sources/upload/product-feed';
      }

      // Use UPLOADS_URL for file uploads to bypass Cloudflare (supports up to 500MB)
      const response = await authFetch(`${UPLOADS_URL}${endpoint}`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Reload data sources
        await loadDataSources();
        alert(`Successfully uploaded ${file.name}\n${result.recordsCount} records imported`);
        // Stay on current tab after upload
        setActiveTab(sourceType);
      } else if (result.uploadRecordSuccess) {
        // Product data record was stored but post-upload processing failed.
        // Reload so the new data source appears, then surface the specific failure.
        await loadDataSources();
        if (result.batchJobError) {
          alert(`File uploaded and products imported, but embedding batch could not be started:\n${result.batchJobError}\n\nProducts are stored — use 'Retry Embeddings' to re-trigger batch generation.`);
        } else {
          // KB import failed — no knowledge_base_entries may exist yet, so Retry Embeddings
          // would find nothing to embed. Point to support instead.
          alert(`File uploaded but product import to knowledge base failed:\n${result.postUploadProcessingError || 'Unknown error'}\n\nThe data source record was created. Please contact support or re-upload the feed.`);
        }
        setActiveTab(sourceType);
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [sourceType]: false }));
      setUploadProgress(prev => ({ ...prev, [sourceType]: 0 }));
    }
  };

  const handleViewData = async (dataSource: DataSource) => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewData(null);

    try {
      const response = await authFetch(
        `${API_URL}/data-sources/preview/${dataSource.id}?siteId=${selectedSite?.id || selectedSite?._id}`
      );
      const data = await response.json();

      if (data.success) {
        setPreviewData({
          dataSource,
          records: data.records,
          totalRecords: data.totalRecords
        });
      } else {
        alert(`Failed to load preview: ${data.error}`);
        setShowPreview(false);
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      alert(`Failed to load preview: ${error.message}`);
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (dataSourceId: string) => {
    if (!selectedSite) return;

    if (!confirm('Are you sure you want to delete this data source? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/data-sources/${dataSourceId}?siteId=${selectedSite.id || selectedSite._id}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        // Reload data sources
        await loadDataSources();
      } else {
        alert(`Delete failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      alert(`Delete failed: ${error.message}`);
    }
  };

  const handleRetryEmbeddings = async (dataSourceId: string) => {
    if (!selectedSite) return;

    const fileStats = getFileEmbeddingStats(dataSourceId);
    if (!fileStats || !fileStats.has_missing_embeddings) return;

    if (!confirm(`Retry generating embeddings for ${fileStats.entries_without_embeddings.toLocaleString()} products?\n\nThis will create a new batch job to generate the missing embeddings.`)) {
      return;
    }

    setRetrying(prev => ({ ...prev, [dataSourceId]: true }));

    try {
      const response = await authFetch(`${API_URL}/data-sources/retry-embeddings/${dataSourceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id || selectedSite._id
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(`Successfully started retry for ${result.productsProcessed.toLocaleString()} products.\n\nBatch Job ID: ${result.batchJobId}`);
        // Reload data sources to show new batch job
        await loadDataSources();
      } else {
        alert(`Retry failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Retry error:', error);
      alert(`Retry failed: ${error.message}`);
    } finally {
      setRetrying(prev => ({ ...prev, [dataSourceId]: false }));
    }
  };

  const handleRefreshStatus = async (batchJobId: string) => {
    setReconciling(prev => ({ ...prev, [batchJobId]: true }));

    try {
      const response = await authFetch(`${API_URL}/data-sources/reconcile-batch/${batchJobId}`, {
        method: 'POST'
      });

      const result = await response.json();

      if (result.success) {
        if (result.reconciled) {
          alert(`Status updated: ${result.message}\nNew status: ${result.newStatus}`);
        } else {
          alert(result.message);
        }
        // Reload data sources to reflect any changes
        await loadDataSources();
      } else {
        alert(`Refresh failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Refresh status error:', error);
      alert(`Refresh failed: ${error.message}`);
    } finally {
      setReconciling(prev => ({ ...prev, [batchJobId]: false }));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'reviews':
        return <FileText className="w-5 h-5" />;
      case 'google_ads':
        return <Database className="w-5 h-5" />;
      case 'product_feed':
        return <ShoppingBag className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'reviews':
        return 'Customer Reviews';
      case 'google_ads':
        return 'Google Ads Search Terms';
      case 'product_feed':
        return 'Product Feed';
      default:
        return sourceType;
    }
  };

  const filterByType = (type: string) => {
    return dataSources.filter(ds => ds.source_type === type);
  };

  const getBatchJobForDataSource = (dataSourceId: string): BatchJob | undefined => {
    return batchJobs.find(job => job.data_source_id === dataSourceId);
  };

  const getFileEmbeddingStats = (dataSourceId: string): FileEmbeddingStats | undefined => {
    return fileEmbeddingStats.find(stats => stats.data_source_id === dataSourceId);
  };

  const getEmbeddingStatsForType = (sourceType: string): EmbeddingStats | undefined => {
    // Map UI source types to database source types
    const sourceTypeMapping: Record<string, string> = {
      'reviews': 'customer_review',
      'google_ads': 'google_ads',
      'product_feed': 'product_feed'
    };

    const dbSourceType = sourceTypeMapping[sourceType] || sourceType;
    return embeddingStats.find(stats => stats.source_type === dbSourceType);
  };

  const renderBatchStatus = (batchJob: BatchJob | undefined, dataSourceId: string) => {
    // Check if this data source has a batch group (auto-chunked upload)
    const batchGroup = batchGroups[dataSourceId];

    // If there's a batch group, show aggregated progress
    if (batchGroup) {
      const isActive = batchGroup.status === 'processing';
      const isCompleted = batchGroup.status === 'completed';
      const isFailed = batchGroup.status === 'failed';
      const isPartial = batchGroup.status === 'partial';

      return (
        <div className="mt-2 p-2 bg-accent/50 rounded text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isActive && <Loader2 className="w-3 h-3 animate-spin" />}
              {isCompleted && <CheckCircle2 className="w-3 h-3 text-green-500" />}
              {isFailed && <AlertCircle className="w-3 h-3 text-red-500" />}
              {isPartial && <AlertCircle className="w-3 h-3 text-orange-500" />}
              <span className="font-medium">
                Processing {batchGroup.total_records.toLocaleString()} records in {batchGroup.total_chunks} chunks
              </span>
            </div>
            <span className="text-muted-foreground">
              {batchGroup.completed_chunks} / {batchGroup.total_chunks} chunks ({batchGroup.progress_percentage}%)
            </span>
          </div>
          {isActive && (
            <div className="mt-2">
              <div className="w-full bg-secondary rounded-full h-1.5">
                <div
                  className="bg-primary h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${batchGroup.progress_percentage}%` }}
                />
              </div>
            </div>
          )}
          {isPartial && (
            <div className="mt-1 text-orange-600">
              {batchGroup.failed_chunks} chunk(s) failed
            </div>
          )}
          {isFailed && (
            <div className="mt-1 text-red-500">
              All {batchGroup.total_chunks} chunks failed
            </div>
          )}
          {isCompleted && (
            <div className="mt-1 text-green-600">
              All {batchGroup.total_chunks} chunks completed successfully
            </div>
          )}
        </div>
      );
    }

    // Get accurate embedding stats for this specific file
    const fileStats = getFileEmbeddingStats(dataSourceId);

    if (!fileStats && !batchJob) return null;

    // Use file stats if available (more accurate), otherwise fall back to batch job
    if (fileStats) {
      const hasEmbeddings = fileStats.entries_with_embeddings > 0;
      const hasMissing = fileStats.has_missing_embeddings;
      const isComplete = !hasMissing && hasEmbeddings;
      const completionPercentage = Number(fileStats.embedding_completion_percentage) || 0;

      return (
        <div className="mt-2 p-2 bg-accent/50 rounded text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasMissing && <AlertCircle className="w-3 h-3 text-orange-500" />}
              {isComplete && <CheckCircle2 className="w-3 h-3 text-green-500" />}
              {!hasEmbeddings && <Loader2 className="w-3 h-3 text-muted-foreground" />}
              <span className="font-medium">
                Embeddings: {isComplete ? 'Complete' : hasMissing ? 'Incomplete' : 'Pending'}
              </span>
            </div>
            <span className={hasMissing ? 'text-orange-600 font-medium' : 'text-muted-foreground'}>
              {fileStats.entries_with_embeddings.toLocaleString()} / {fileStats.kb_entries_created.toLocaleString()} ({completionPercentage.toFixed(1)}%)
            </span>
          </div>
          {hasMissing && (
            <div className="mt-1 flex items-center justify-between">
              <span className="text-orange-600">
                ⚠ {fileStats.entries_without_embeddings.toLocaleString()} products missing embeddings
              </span>
            </div>
          )}
          {isComplete && (
            <div className="mt-1 text-green-600">
              ✓ All {fileStats.kb_entries_created.toLocaleString()} products have embeddings
            </div>
          )}
        </div>
      );
    }

    // Fallback to batch job status if file stats not available
    if (!batchJob) return null;

    const isActive = ['preparing', 'uploading', 'queued', 'submitting', 'submitted', 'validating', 'in_progress', 'finalizing', 'rate_limited'].includes(batchJob.status);
    const isCompleted = batchJob.status === 'completed';
    const isFailed = batchJob.status === 'failed';
    const isQueued = batchJob.status === 'queued' || batchJob.status === 'rate_limited';

    // Calculate progress from batch job if available
    const hasProgress = batchJob.total_records && batchJob.total_records > 0;
    const progressPercentage = hasProgress
      ? Math.round(((batchJob.processed_records || 0) / batchJob.total_records) * 100)
      : 0;

    // User-friendly status messages for queue states
    const getStatusMessage = () => {
      switch (batchJob.status) {
        case 'queued':
          return 'Queued (waiting for OpenAI quota)';
        case 'rate_limited':
          return 'Rate limited (will retry automatically)';
        case 'submitting':
          return 'Submitting to OpenAI...';
        default:
          return batchJob.status.replace('_', ' ');
      }
    };

    return (
      <div className="mt-2 p-2 bg-accent/50 rounded text-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isActive && <Loader2 className="w-3 h-3 animate-spin" />}
            {isCompleted && <CheckCircle2 className="w-3 h-3 text-green-500" />}
            {isFailed && <AlertCircle className="w-3 h-3 text-red-500" />}
            <span className="font-medium">
              {isCompleted ? 'Embeddings generated successfully' : `Processing: ${getStatusMessage()}`}
            </span>
          </div>
          {hasProgress && isActive && !isQueued && (
            <span className="text-muted-foreground">
              {(batchJob.processed_records || 0).toLocaleString()} / {batchJob.total_records.toLocaleString()} ({progressPercentage}%)
            </span>
          )}
        </div>
        {hasProgress && isActive && !isQueued && (
          <div className="mt-2">
            <div className="w-full bg-secondary rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}
        {isQueued && (
          <div className="mt-1 text-blue-600">
            💡 Batch is queued and will be submitted automatically when capacity is available.
          </div>
        )}
        {isFailed && (
          <div className="mt-1 space-y-1">
            {batchJob.error_message && (
              <div className="text-red-500">
                Error: {batchJob.error_message}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                onClick={() => !reconciling[batchJob.id] && handleRefreshStatus(batchJob.id)}
                title="Check OpenAI for actual status"
                className={`cursor-pointer text-blue-600 border-blue-300 hover:bg-blue-50 ${
                  reconciling[batchJob.id] ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {reconciling[batchJob.id] ? (
                  <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1 inline" />
                )}
                Refresh Status
              </Badge>
              <span className="text-xs text-muted-foreground">
                Check if batch actually completed in OpenAI
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEmbeddingStats = (sourceType: string, showOnce: boolean = false) => {
    const stats = getEmbeddingStatsForType(sourceType);
    if (!stats || stats.total_entries === 0) return null;

    // Only show once per section if requested (to avoid duplication)
    const sourceTypeName = getSourceTypeLabel(sourceType);

    const isComplete = stats.completion_percentage === 100;
    const isInProgress = stats.completion_percentage > 0 && stats.completion_percentage < 100;

    return (
      <div className="mt-2 p-2 bg-accent/50 rounded text-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isInProgress && <Loader2 className="w-3 h-3 animate-spin" />}
            {isComplete && <CheckCircle2 className="w-3 h-3 text-green-500" />}
            <span className="font-medium">
              {showOnce ? `${sourceTypeName} Embeddings` : 'Embeddings'}: {isComplete ? 'Complete' : isInProgress ? 'In Progress' : 'Not Started'}
            </span>
          </div>
          <span className="text-muted-foreground">
            {stats.entries_with_embeddings.toLocaleString()} / {stats.total_entries.toLocaleString()} ({stats.completion_percentage.toFixed(1)}%)
          </span>
        </div>
        {isInProgress && (
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.completion_percentage}%` }}
            />
          </div>
        )}
        {showOnce && stats.entries_without_embeddings > 0 && (
          <div className="mt-1 text-muted-foreground">
            {stats.entries_without_embeddings.toLocaleString()} entries need embeddings
          </div>
        )}
        {showOnce && isInProgress && (
          <div className="mt-1 text-blue-600 text-xs italic">
            Generating embeddings for all {sourceTypeName.toLowerCase()} across all uploaded files...
          </div>
        )}
      </div>
    );
  };

  const DataSourceUpload = ({ sourceType, title, description, acceptedFormats }: any) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getSourceIcon(sourceType)}
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Show embedding stats once per section */}
            {renderEmbeddingStats(sourceType, true)}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFormats}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(sourceType, file);
                  }
                }}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading[sourceType]}
                className="w-full"
              >
                {uploading[sourceType] ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading... {uploadProgress[sourceType]}%
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {title}
                  </>
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Accepted formats: {acceptedFormats}
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Files</h4>
              {filterByType(sourceType).length === 0 ? (
                <p className="text-sm text-muted-foreground">No files uploaded yet</p>
              ) : (
                <div className="space-y-2">
                  {filterByType(sourceType).map((ds) => (
                    <div
                      key={ds.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {getStatusIcon(ds.status)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{ds.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(ds.file_size)} • {ds.records_count.toLocaleString()} records • {formatDate(ds.upload_date)}
                          </p>
                          {sourceType === 'product_feed' && renderBatchStatus(getBatchJobForDataSource(ds.id), ds.id)}
                        </div>
                        <Badge variant={ds.status === 'completed' ? 'default' : ds.status === 'processing' ? 'secondary' : 'destructive'}>
                          {ds.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Show retry button only if there are missing embeddings AND no active batch job */}
                        {sourceType === 'product_feed' && (() => {
                          const fileStats = getFileEmbeddingStats(ds.id);
                          const batchJob = getBatchJobForDataSource(ds.id);
                          const hasMissing = fileStats?.has_missing_embeddings;
                          const isJobActive = batchJob && ['preparing', 'uploading', 'queued', 'submitting', 'submitted', 'validating', 'in_progress', 'finalizing', 'processing_results', 'rate_limited'].includes(batchJob.status);

                          // Show retry button only if there are missing embeddings and no active job
                          if (hasMissing && !isJobActive) {
                            return (
                              <Badge
                                variant="outline"
                                onClick={() => !retrying[ds.id] && handleRetryEmbeddings(ds.id)}
                                title="Retry generating missing embeddings"
                                className={`cursor-pointer text-orange-600 border-orange-300 hover:bg-orange-50 ${
                                  retrying[ds.id] ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {retrying[ds.id] ? (
                                  <Loader2 className="w-3 h-3 mr-1 inline animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3 mr-1 inline" />
                                )}
                                Retry Embeddings
                              </Badge>
                            );
                          }

                          return null;
                        })()}
                        {ds.status === 'completed' && (
                          <>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleViewData(ds)}
                              title="View data preview"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {sourceType === 'product_feed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedDataSource(ds);
                                  setNormalizationModalOpen(true);
                                }}
                                title="Normalize attribute values"
                              >
                                <Sparkles className="w-4 h-4 mr-1" />
                                Normalize
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(ds.id)}
                          className="ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>No Site Selected</CardTitle>
            <CardDescription>
              Please select a site from the header to manage data sources.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Data Sources</h1>
        <p className="text-muted-foreground">
          Upload and manage data sources for enhanced question generation and SEO insights
        </p>
      </div>

      {/* Real-time Progress Alert */}
      {progressMessage && (
        <Alert className="bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            <div className="flex-1">
              <div className="font-medium text-blue-900">
                {progressPhase.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
              <AlertDescription className="text-blue-800 text-sm">
                {progressMessage}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="google_ads">Google Ads</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="product_feed">Product Feed</TabsTrigger>
          </TabsList>

          <TabsContent value="google_ads">
            <DataSourceUpload
              sourceType="google_ads"
              title="Google Ads Search Terms"
              description="Upload your Google Ads search terms report to generate questions from high-value keywords"
              acceptedFormats=".csv"
            />
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Reviews Upload
                </CardTitle>
                <CardDescription>
                  Upload product reviews to enrich content generation and understand customer preferences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Review Type Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="review-subtype">Review Type</Label>
                    <Select value={reviewSubtype} onValueChange={setReviewSubtype}>
                      <SelectTrigger id="review-subtype">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technical_reviews">
                          Technical Reviews (Product Q&As)
                        </SelectItem>
                        <SelectItem value="customer_reviews">
                          Customer Reviews (Product Ratings & Feedback)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Usage Information Alert */}
                  {reviewSubtype === 'technical_reviews' ? (
                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-900 text-sm">
                        <strong>Technical Reviews</strong> contain product questions and expert answers.
                        These will be used to <strong>directly answer customer questions</strong> in the chatbot,
                        providing accurate product-specific information.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert className="bg-amber-50 border-amber-200">
                      <Info className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-900 text-sm">
                        <strong>Customer Reviews</strong> contain general product ratings and feedback.
                        These will <strong>only be used when researching customer interest and preferences</strong>,
                        not for directly answering questions in the chatbot.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* File Upload */}
                  <DataSourceUpload
                    sourceType="reviews"
                    title="Reviews File"
                    description="Upload your reviews file in XML or CSV format"
                    acceptedFormats=".xml,.csv"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="product_feed">
            <div className="flex justify-between items-center mb-4">
              <div />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEnrichmentSettingsOpen(true)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Enrichment Settings
              </Button>
            </div>
            {suspiciousProductsCount > 0 && (
              <Alert className="mb-4 border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="flex items-center justify-between">
                  <span className="text-amber-800">
                    <strong>{suspiciousProductsCount}</strong> products have enriched attributes that need review
                  </span>
                  <Link to="/research/enrichment-review">
                    <Button variant="outline" size="sm" className="ml-4">
                      Review Now
                    </Button>
                  </Link>
                </AlertDescription>
              </Alert>
            )}
            <DataSourceUpload
              sourceType="product_feed"
              title="Product Feed"
              description="Upload your product catalog to enrich question generation with product-specific insights"
              acceptedFormats=".xml,.csv"
            />
          </TabsContent>
        </Tabs>
      )}

      {!loading && dataSources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>All Data Sources</CardTitle>
            <CardDescription>
              Complete list of all uploaded data sources for {selectedSite.domain || selectedSite.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dataSources.map((ds) => (
                <div
                  key={ds.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {getSourceIcon(ds.source_type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{ds.file_name}</p>
                        <Badge variant="outline">{getSourceTypeLabel(ds.source_type)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(ds.file_size)} • {ds.records_count.toLocaleString()} records • {formatDate(ds.upload_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(ds.status)}
                      <Badge variant={ds.status === 'completed' ? 'default' : ds.status === 'processing' ? 'secondary' : 'destructive'}>
                        {ds.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ds.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewData(ds)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Data
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(ds.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Data Preview: {previewData?.dataSource?.file_name}</DialogTitle>
            <DialogDescription>
              Showing first 100 records of {previewData?.totalRecords?.toLocaleString() || 0} total records
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : previewData ? (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(previewData.records[0] || {}).map((key) => (
                      <TableHead key={key} className="whitespace-nowrap">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.records.map((record: any, idx: number) => (
                    <TableRow key={idx}>
                      {Object.values(record).map((value: any, cellIdx: number) => (
                        <TableCell key={cellIdx} className="max-w-xs truncate">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value || '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center text-muted-foreground">No data available</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Feed Normalization Modal */}
      {selectedDataSource && (
        <ProductFeedNormalizationModal
          open={normalizationModalOpen}
          onClose={() => {
            setNormalizationModalOpen(false);
            setSelectedDataSource(null);
            // Reload data sources to reflect changes
            loadDataSources();
          }}
          dataSourceId={selectedDataSource.id}
          dataSourceName={selectedDataSource.file_name}
        />
      )}

      {/* Enrichment Settings Modal */}
      {selectedSite && (
        <EnrichmentSettingsModal
          siteId={selectedSite.id}
          isOpen={enrichmentSettingsOpen}
          onClose={() => setEnrichmentSettingsOpen(false)}
        />
      )}
    </div>
  );
}
